import React, { useState, ChangeEventHandler, useEffect, useCallback } from "react";
import GlobalSearch from "../common/GlobalSearch";
import { RefreshSpinner } from "../common/ProjectIcons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SelectInput from "../common/SelectInput";
import { useTranslation } from "react-i18next";
import { DateRange, LogLevel, LogTypes } from "../common/ObjectTypes";
import { isValidDuration } from "../../utils/parseDuration";
import { filterClasses, buttonClasses } from "../common/tailwindClassnames";
import { LOG_LEVEL_ICONS } from './logLevelIcons';

type LogLevelOption = { label: string; value: string; [key: string]: any };

const formatLogLevelOption = (option: LogLevelOption) => (
    <div className="flex items-center gap-2">
        {LOG_LEVEL_ICONS[option.value]}
        <span>{option.label}</span>
    </div>
);

const SESSION_STORAGE_KEY = "routeLogsDateFilter";

function parseOffsetMinutes(offset: string): number {
    const match = offset.match(/^([+-])(\d{1,2}):?(\d{2})$/);
    if (!match) return 0;
    const sign = match[1] === "+" ? 1 : -1;
    return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
}

function toTzDisplay(utcDate: Date, offsetStr: string): Date {
    const targetOffsetMin = parseOffsetMinutes(offsetStr);
    const browserOffsetMin = -utcDate.getTimezoneOffset();
    const diffMs = (targetOffsetMin - browserOffsetMin) * 60 * 1000;
    return new Date(utcDate.getTime() + diffMs);
}

function fromTzDisplay(displayDate: Date, offsetStr: string): Date {
    const targetOffsetMin = parseOffsetMinutes(offsetStr);
    const browserOffsetMin = -displayDate.getTimezoneOffset();
    const diffMs = (targetOffsetMin - browserOffsetMin) * 60 * 1000;
    return new Date(displayDate.getTime() - diffMs);
}

function formatTzDate(utcDate: Date, offsetStr: string): string {
    const d = toTzDisplay(utcDate, offsetStr);
    const mon = d.toLocaleString("en", { month: "short" });
    const day = d.getDate();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${mon} ${day} ${hh}:${mm}`;
}

type LogsFilterProps = {
    globalSearchStr?: string,
    timezones: any[],
    selectedTimezone: any,
    onChangeGlobalSearch: (value: string) => void,
    onResetFilters: any,
    onChangeStatusFilter: ChangeEventHandler,
    onChangeLogType: ChangeEventHandler,
    onChangeLogDate: Function,
    onSelectTimezone: Function,
    onRefresh: Function,
    reloadLogs: boolean,
}

const LogsFilter = ({
    globalSearchStr,
    timezones,
    selectedTimezone,
    onChangeGlobalSearch,
    onResetFilters,
    onChangeStatusFilter,
    onChangeLogType,
    onChangeLogDate,
    onSelectTimezone,
    onRefresh,
    reloadLogs,
}: LogsFilterProps) => {
    const { t } = useTranslation();
    const defaultDateRange = DateRange.find(option => option.label === "Past 3 hours");
    const [selectedLogType, setSelectedLogType] = useState<any>(null);
    const [selectedLogLevel, setSelectedLogLevel] = useState<any>(null);
    const [selectedDateRange, setSelectedDateRange] = useState<any>(defaultDateRange || null);

    const [showCustomPanel, setShowCustomPanel] = useState(false);
    const [relativeTimeInput, setRelativeTimeInput] = useState("");
    const [relativeTimeError, setRelativeTimeError] = useState("");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [dateError, setDateError] = useState("");
    const [dateSearchInput, setDateSearchInput] = useState("");

    const tzOffset = selectedTimezone?.value || "+00:00";

    const saveFilterToSession = useCallback((data: Record<string, any>) => {
        try {
            sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
        } catch { /* ignore quota errors */ }
    }, []);

    const clearFilterSession = useCallback(() => {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }, []);

    // Restore saved filter from sessionStorage on mount
    useEffect(() => {
        let restored = false;
        try {
            const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (raw) {
                const saved = JSON.parse(raw);
                if (saved.type === "preset" && saved.value) {
                    const preset = DateRange.find(o => o.value === saved.value);
                    if (preset) {
                        setSelectedDateRange(preset);
                        onChangeLogDate(preset, "from_date");
                        onChangeLogDate(null, "to_date");
                        restored = true;
                    }
                } else if (saved.type === "relative" && saved.input) {
                    setSelectedDateRange({ label: `Past ${saved.input}`, value: "custom" });
                    setRelativeTimeInput(saved.input);
                    onChangeLogDate({ value: saved.input }, "from_date");
                    onChangeLogDate(null, "to_date");
                    restored = true;
                } else if (saved.type === "custom" && saved.startDate) {
                    const sd = new Date(saved.startDate);
                    setStartDate(sd);
                    onChangeLogDate({ value: sd }, "from_date");
                    const savedOffset = selectedTimezone?.value || "+00:00";
                    if (saved.endDate) {
                        const ed = new Date(saved.endDate);
                        setEndDate(ed);
                        onChangeLogDate({ value: ed }, "to_date");
                        const startLabel = formatTzDate(sd, savedOffset);
                        const endLabel = formatTzDate(ed, savedOffset);
                        setSelectedDateRange({ label: `${startLabel} – ${endLabel}`, value: "custom" });
                    } else {
                        const startLabel = formatTzDate(sd, savedOffset);
                        setSelectedDateRange({ label: `${startLabel} –`, value: "custom" });
                    }
                    restored = true;
                }
            }
        } catch { /* ignore parse errors */ }

        if (!restored && defaultDateRange) {
            onChangeLogDate(defaultDateRange, "from_date");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDateSearchInputChange = (inputValue: string) => {
        setDateSearchInput(inputValue);
    };

    const handleDateRangeKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== "Enter") return;
        const value = dateSearchInput.trim();
        if (!value || !isValidDuration(value)) return;

        e.preventDefault();
        setRelativeTimeError("");
        setStartDate(null);
        setEndDate(null);
        onChangeLogDate({ value }, "from_date");
        onChangeLogDate(null, "to_date");
        setSelectedDateRange({ label: `Past ${value}`, value: "custom" });
        setShowCustomPanel(false);
        setDateSearchInput("");
        saveFilterToSession({ type: "relative", input: value });

        (e.target as HTMLElement).blur();
    };

    const dateRangeNoOptionsMessage = ({ inputValue }: { inputValue: string }) => {
        if (inputValue && isValidDuration(inputValue.trim())) {
            const value = t("Press Enter to apply");
            return value + ` "${inputValue.trim()}"`;
        }
        return t("No options");
    };

    const handleLogTypeChange = (e: any) => {
        setSelectedLogType(e);
        onChangeLogType(e);
    };

    const handleLogLevelChange = (e: any) => {
        setSelectedLogLevel(e);
        onChangeStatusFilter(e);
    };

    const handleDateRangeChange = (e: any) => {
        setSelectedDateRange(e);

        if (!e) {
            setShowCustomPanel(false);
            setRelativeTimeInput("");
            setRelativeTimeError("");
            setStartDate(null);
            setEndDate(null);
            onChangeLogDate(null, "from_date");
            onChangeLogDate(null, "to_date");
            clearFilterSession();
            return;
        }

        if (e.value === "custom") {
            setShowCustomPanel(true);
            saveFilterToSession({ type: "preset", value: "custom" });
            return;
        }

        setShowCustomPanel(false);
        setRelativeTimeInput("");
        setRelativeTimeError("");
        setStartDate(null);
        setEndDate(null);
        onChangeLogDate(e, "from_date");
        onChangeLogDate(null, "to_date");
        saveFilterToSession({ type: "preset", value: e.value, label: e.label });
    };

    const handleApplyRelativeTime = () => {
        const value = relativeTimeInput.trim();
        if (!value) return;

        if (!isValidDuration(value)) {
            const error_message = t("Invalid format. Use e.g. 1h, 90m, 60s, 1d, 2w");
            setRelativeTimeError(error_message);
            return;
        }

        setRelativeTimeError("");
        setStartDate(null);
        setEndDate(null);
        onChangeLogDate({ value }, "from_date");
        onChangeLogDate(null, "to_date");
        setSelectedDateRange({ label: `Past ${value}`, value: "custom" });
        setShowCustomPanel(false);
        saveFilterToSession({ type: "relative", input: value });
    };

    const handleRelativeTimeKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleApplyRelativeTime();
        }
    };

    const handleStartDateChange = (displayDate: Date) => {
        const utcDate = fromTzDisplay(displayDate, tzOffset);
        setStartDate(utcDate);
        setRelativeTimeInput("");
        setRelativeTimeError("");
        setDateError("");
        onChangeLogDate({ value: utcDate }, "from_date");
        const startLabel = formatTzDate(utcDate, tzOffset);
        if (endDate && utcDate > endDate) {
            setEndDate(null);
            onChangeLogDate(null, "to_date");
            setSelectedDateRange({ label: `${startLabel} –`, value: "custom" });
            saveFilterToSession({
                type: "custom",
                startDate: utcDate.toISOString()
            });
        } else if (endDate) {
            const endLabel = formatTzDate(endDate, tzOffset);
            setSelectedDateRange({ label: `${startLabel} – ${endLabel}`, value: "custom" });
            saveFilterToSession({
                type: "custom",
                startDate: utcDate.toISOString(),
                endDate: endDate.toISOString()
            });
        } else {
            setSelectedDateRange({ label: `${startLabel} –`, value: "custom" });
            saveFilterToSession({
                type: "custom",
                startDate: utcDate.toISOString()
            });
        }
    };

    const handleEndDateChange = (displayDate: Date) => {
        const utcDate = fromTzDisplay(displayDate, tzOffset);
        if (startDate && utcDate < startDate) {
            const error = t("End date must be after start date");
            setDateError(error);
            return;
        }
        setEndDate(utcDate);
        setRelativeTimeInput("");
        setRelativeTimeError("");
        setDateError("");
        onChangeLogDate({ value: utcDate }, "to_date");
        const endLabel = formatTzDate(utcDate, tzOffset);
        const startLabel = startDate ? formatTzDate(startDate, tzOffset) : "";
        setSelectedDateRange({ label: `${startLabel} – ${endLabel}`, value: "custom" });
        setShowCustomPanel(false);
        saveFilterToSession({
            type: "custom",
            startDate: startDate ? startDate.toISOString() : "",
            endDate: utcDate.toISOString()
        });
    };

    const handleResetFilters = () => {
        setSelectedLogType(null);
        setSelectedLogLevel(null);
        setShowCustomPanel(false);
        setRelativeTimeInput("");
        setRelativeTimeError("");
        setDateError("");
        setStartDate(null);
        setEndDate(null);

        onResetFilters();
        clearFilterSession();

        const defaultOption = DateRange.find(option => option.label === "Past 3 hours");
        if (defaultOption) {
            setSelectedDateRange(defaultOption);
            onChangeLogDate(defaultOption, "from_date");
        }
    };

    return (
        <div className="w-full text-off-black bg-very-light-gray mr-3 mb-3 h-auto px-3 pb-3 transition-all duration-300">
            {/* Search row */}
            <div className="flex flex-row items-end gap-3 pt-3 mb-3">
                <GlobalSearch
                    onChangeGlobalSearch={onChangeGlobalSearch}
                    savedString={globalSearchStr}
                />
                {!globalSearchStr && (
                    <div className="flex flex-row gap-1 mb-2 items-center flex-wrap">
                        <span className="text-secondary-gray text-xs">{t("Try")}</span>
                        {[
                            { label: t("delivered"), value: "delivered" },
                            { label: t("source"), value: "source" },
                            { label: t("event"), value: "event" },
                            { label: t("observation"), value: "observation" },
                        ].map(({ label, value }) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => onChangeGlobalSearch(value)}
                                className="text-xs px-2 py-0.5 rounded-full border border-gray-lines text-secondary-gray hover:bg-light-green transition-colors duration-300"
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}
                <button
                    className={`${buttonClasses.lightGreen} whitespace-nowrap h-[38px] mb-2 ml-auto`}
                    onClick={handleResetFilters}
                >
                    {t("Clear All Filters")}
                </button>
            </div>

            {/* Filters row */}
            <div className="flex flex-row justify-between items-start">
                <div className="flex flex-row flex-wrap gap-x-2">
                    {/* log type */}
                    <div>
                        <label className="text-secondary-gray text-sm">{t("Log Type")}</label>
                        <SelectInput
                            options={LogTypes.map(option => ({
                                ...option,
                                label: t(option.label, option.label)
                            }))}
                            data-testid="select_timezone_activity_logs_filter"
                            className={"basic-single pb-1 w-60 text-sm"}
                            defaultValue={undefined}
                            value={selectedLogType ? {
                                ...selectedLogType,
                                label: t(selectedLogType.label, selectedLogType.label)
                            } : null}
                            isDisabled={false}
                            isLoading={false}
                            isClearable={true}
                            isSearchable={true}
                            isMulti={false}
                            name={"Filter by Organization"}
                            onChange={handleLogTypeChange}
                            variant="routeFilter"
                        />
                    </div>
                    {/* status */}
                    <div>
                        <label className="text-secondary-gray text-sm">{t("Minimum Log Level")}</label>
                        <SelectInput
                            options={LogLevel.map(option => ({
                                ...option,
                                label: t(option.label, option.label)
                            }))}
                            data-testid="select_timezone_activity_logs_filter"
                            className={"basic-single pb-1 w-60 text-sm"}
                            defaultValue={undefined}
                            value={selectedLogLevel ? {
                                ...selectedLogLevel,
                                label: t(selectedLogLevel.label, selectedLogLevel.label)
                            } : null}
                            isDisabled={false}
                            isLoading={false}
                            isClearable={true}
                            isSearchable={true}
                            isMulti={false}
                            name={"Filter by Organization"}
                            onChange={handleLogLevelChange}
                            variant="routeFilter"
                            formatOptionLabel={formatLogLevelOption}
                        />
                    </div>
                    {/* date & time */}
                    <div className="relative">
                        <label className="text-secondary-gray text-sm">{t("Date Range")}</label>
                        <SelectInput
                            options={DateRange.map(option => ({
                                ...option,
                                label: t(option.label, option.label)
                            }))}
                            data-testid="select_timezone_activity_logs_filter"
                            className={"basic-single pb-1 w-80 text-sm"}
                            value={selectedDateRange ? {
                                ...selectedDateRange,
                                label: t(selectedDateRange.label, selectedDateRange.label)
                            } : null}
                            isDisabled={false}
                            isLoading={false}
                            isClearable={true}
                            isSearchable={true}
                            isMulti={false}
                            name={"Date Range"}
                            onChange={handleDateRangeChange}
                            variant="routeFilter"
                            handleInputChange={handleDateSearchInputChange}
                            onKeyDown={handleDateRangeKeyDown}
                            noOptionsMessage={dateRangeNoOptionsMessage}
                        />
                        {showCustomPanel && (
                            <div className="absolute top-full left-0 mt-2 z-20 bg-very-light-gray border border-gray-lines rounded p-3 w-80 transition-colors duration-300">
                                <label className="text-secondary-gray text-xs">{t("Relative time")}</label>
                                <div className="flex flex-row gap-1 mt-1">
                                    <input
                                        type="text"
                                        value={relativeTimeInput}
                                        onChange={(e) => {
                                            setRelativeTimeInput(e.target.value);
                                            setRelativeTimeError("");
                                        }}
                                        onKeyDown={handleRelativeTimeKeyDown}
                                        placeholder={t("e.g. 1h, 90m, 60s, 1d")}
                                        className="border border-gray-lines rounded-sm p-1 text-sm flex-1 min-w-0 transition-colors duration-300"
                                    />
                                    <button
                                        onClick={handleApplyRelativeTime}
                                        className={`${buttonClasses.lightGreen} text-sm`}
                                    >
                                        {t("Apply")}
                                    </button>
                                </div>
                                {relativeTimeError && (
                                    <p className="text-red-500 text-xs mt-1">{relativeTimeError}</p>
                                )}
                                <div className="flex items-center my-3">
                                    <div className="flex-1 border-t border-gray-lines"></div>
                                    <span className="px-2 text-secondary-gray text-xs">{t("or")}</span>
                                    <div className="flex-1 border-t border-gray-lines"></div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <div className="flex flex-col">
                                        <label className="text-secondary-gray text-xs">{t("Start")}</label>
                                        <DatePicker
                                            selected={startDate ? toTzDisplay(startDate, tzOffset) : null}
                                            onChange={handleStartDateChange}
                                            showTimeSelect
                                            timeIntervals={15}
                                            className="border border-gray-lines rounded-sm p-1 mt-1 text-sm w-full transition-colors duration-300"
                                            dateFormat="yyyy-MM-dd HH:mm"
                                            placeholderText={t("Select start date")}
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-secondary-gray text-xs">{t("End")}</label>
                                        <DatePicker
                                            selected={endDate ? toTzDisplay(endDate, tzOffset) : null}
                                            minDate={startDate ? toTzDisplay(startDate, tzOffset) : undefined}
                                            onChange={handleEndDateChange}
                                            disabled={!startDate}
                                            showTimeSelect
                                            timeIntervals={15}
                                            className="border border-gray-lines rounded-sm p-1 mt-1 text-sm w-full transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                            dateFormat="yyyy-MM-dd HH:mm"
                                            placeholderText={t("Select end date")}
                                        />
                                    </div>
                                </div>
                                {dateError && (
                                    <p className="text-red-500 text-xs mt-1">{dateError}</p>
                                )}
                                <p className="text-secondary-gray text-xs mt-2 mb-1">
                                    {selectedTimezone?.label || "UTC"}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => onRefresh()}
                    aria-label={t("Refresh logs" as any)}
                    disabled={reloadLogs}
                    className={`flex justify-center items-center w-8 h-8 cursor-pointer self-end mb-1 ${reloadLogs ? 'animate-spin origin-center' : ''} transition-colors duration-300`}
                >
                    <RefreshSpinner />
                </button>
            </div>
        </div>
    )
}

export default LogsFilter;
